/**
 * Shared hook for computing exposure-weighted general volatility.
 * Used by both Dashboard and Strategy pages for consistent general-mode risk data.
 *
 * Per-material volatility comes from materialsMap (Postgres).
 * General volatility = exposure-weighted average of per-formula volatilities.
 */
import React from 'react';
import { useMaterialsStore } from '../components/Stores/MaterialsStore';
import { useExposureCalculation } from './useExposureCalculation';

export interface GeneralRisk {
  volatility: number;
  volatilityMean: number;
}

export const useGeneralRisk = (): GeneralRisk => {
  const { materialsMap } = useMaterialsStore();
  const { exposureByFormula } = useExposureCalculation({ useHorizon: true });

  return React.useMemo(() => {
    if (!exposureByFormula) return { volatility: 0, volatilityMean: 0 };
    let totalWeight = 0, sigmaWeighted = 0, muWeighted = 0;
    for (const [formulaId, breakdown] of Object.entries(exposureByFormula)) {
      const mat = materialsMap[formulaId];
      if (!mat || typeof mat.volatility !== 'number') continue;
      const weight = Math.abs(breakdown.netExposure.valueBase);
      totalWeight += weight;
      sigmaWeighted += weight * mat.volatility;
      muWeighted += weight * Math.abs(mat.volatilityMean ?? 0);
    }
    if (totalWeight === 0) return { volatility: 0, volatilityMean: 0 };
    return { volatility: sigmaWeighted / totalWeight, volatilityMean: muWeighted / totalWeight };
  }, [exposureByFormula, materialsMap]);
};
